import React, { useState, useEffect, useContext } from 'react';
import { withStyles } from '@material-ui/core/styles';
import {
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  FormGroup,
  Grid,
  TextField,
  Checkbox,
  Typography,
  RadioGroup,
  Radio,
} from '@material-ui/core';
import classnames from 'classnames';
import * as Yup from 'yup';

// SERVICES
import {
  updateTerritoryInfo,
  getAssignedTerritoryInfo,
  getTerritoryOptions,
  getInspector,
  putInspectorUser,
  createInspectorUser,
  getInspectorProfile,
  saveInspectorProfile,
  getInspectorProfileSkills,
} from '../../../services';
import { Autocomplete } from '../../Autocomplete';
import { AppContext } from '../../../AppContext';
import { ErrorMessage, FastField, FieldArray, Form, Formik, useField } from 'formik';
import Close from '@material-ui/icons/Close';
import Loading from '../../../components/Loading';
import { Select } from '../../FormFields';
import { toBase64 } from '../../../helpers';
import { hasPermission } from '../../../helpers/roles';
import roles from '../../../helpers/roles';

const styles = theme => ({
  root: {
    height: '100%',
  },

  Tabs: {
    '& .MuiTab-root': {
      padding: 20,
    },
  },

  formHeaderServiceContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',

    '& span': {
      flex: 1,
      textAlign: 'left',
    },
  },

  formTextFieldInput: {
    padding: 15,
  },
  formTextFieldNotchedOutline: {
    '& fieldset': {
      borderColor: 'var(--jmi-blue) !important',
    },
  },
  errorMessage: {
    color: theme.palette.secondary.main,
    textAlign: 'center',
  },
});

// const getBase64Image = img => {
//   var canvas = document.createElement('canvas');
//   canvas.width = img.width;
//   canvas.height = img.height;
//   var ctx = canvas.getContext('2d');
//   ctx.drawImage(img, 0, 0);
//   var dataURL = canvas.toDataURL('image/png');
//   return dataURL.replace(/^data:image\/(png|jpg);base64,/, '');
// };

const FormTextField = withStyles(styles)(({ formik, classes, inputProps, InputProps, label, isReadOnly, ...props }) => {
  const { name, onChange } = props;
  const [field] = useField(props);

  return (
    <TextField
      name={name}
      label={label}
      autoComplete={name}
      margin="none"
      variant="outlined"
      fullWidth
      disabled={isReadOnly}
      inputProps={{
        ...inputProps,
        className: classes.formTextFieldInput,
      }}
      InputProps={{
        ...InputProps,
        classes: {
          ...InputProps?.classes,
          focused: classes.formTextFieldNotchedOutline,
        },
      }}
      {...props}
      {...field}
      onChange={onChange || field.onChange}
    />
  );
});

const FormCheckbox = withStyles(styles)(({ classes, formik, label, isReadOnly, ...props }) => {
  const { name } = props;
  const checked = formik.values[name];
  const [field] = useField(props);

  return (
    <FormControlLabel
      control={
        <Checkbox
          name={name}
          checked={checked}
          disabled={isReadOnly}
          // onChange={formik.handleChange}
          {...props}
          {...field}
        />
      }
      label={label}
    />
  );
});

const FormControlWrapper = ({ children, ...props }) => (
  <Grid item xs={12} sm={6} md={4} {...props}>
    {/* <FormControl fullWidth> */}
    {/*  */}
    {children}
    {/* </FormControl> */}
  </Grid>
);

export const Territory = withStyles(styles)(props => {
  const { inspectorId } = props;

  const [territoryInfo, setTerritoryInfo] = useState(null);
  const [territoryOptions, setTerritoryOptions] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const { dispatch } = useContext(AppContext);

  const getTerritoryOptionsAsync = async (territoryInfo = {}) => {
    const { states, cities, counties, zipCodes } = territoryInfo;
    try {
      const options = await getTerritoryOptions(states, cities, counties, zipCodes);

      // go through the results and make sure each smaller level belongs to a selected option at the level above (a zip code must have it's accompanying county in the box above)
      const tempTerritoryInfo = { ...territoryInfo };

      tempTerritoryInfo.counties = tempTerritoryInfo.counties.filter(existingCountyId =>
        options.counties.find(countyOpt => countyOpt.value === existingCountyId),
      );

      tempTerritoryInfo.zipCodes = tempTerritoryInfo.zipCodes.filter(existingZipCodeId =>
        options.zipCodes.find(zipCodeOpt => zipCodeOpt.value === existingZipCodeId),
      );

      setTerritoryInfo(tempTerritoryInfo);
      setTerritoryOptions(options);
    } catch (error) {
      console.log(error);
    }
  };

  const getAssignedInfo = async cb => {
    try {
      const assigned = await getAssignedTerritoryInfo(inspectorId, null);
      await setTerritoryInfo(assigned);
      cb && cb(assigned);
    } catch (error) {
      console.log(error);
    }
  };

  const updateTerritoryInfoAsync = async formData => {
    // const { states, cities, counties, zipCodes } = formData;

    // const territoryInfo = Object.assign({}, formData);
    territoryInfo.inspectorId = parseInt(inspectorId);
    // territoryInfo.zipCodes = zipCodes || [];
    // territoryInfo.cities = cities || [];
    // territoryInfo.counties = counties || [];
    // territoryInfo.states = states || [];

    try {
      setIsSaving(true);
      const res = await updateTerritoryInfo(territoryInfo);

      dispatch({
        type: 'SET_SNACKBAR',
        snackbar: {
          isOpen: true,
          message: res.message || 'Saved successfully',
          variant: 'success',
        },
      });
    } catch (error) {
      dispatch({
        type: 'SET_SNACKBAR',
        snackbar: {
          isOpen: true,
          message: error.message || 'Failed to save zip code assignment',
          variant: 'error',
        },
      });
    }
    setIsSaving(false);
  };

  const getSelectedValue = (options, selectedIds) => {
    const returnVal = options && selectedIds ? options.filter(opt => selectedIds.includes(opt.value)) : [];
    // console.log(returnVal, value, options);
    return returnVal;
  };

  useEffect(() => {
    const getData = async () => {
      await getAssignedInfo(getTerritoryOptionsAsync);
      // await getTerritoryOptions();
    };
    getData();
  }, []);

  return (
    <div
      style={{
        opacity: isSaving ? 0.6 : 1,
        pointerEvents: isSaving ? 'none' : 'auto',
      }}
    >
      <p>
        Warning! Any select boxes that have over 1,000 options will see a performance decrease. Select options from the
        broader categories (e.g. 'State') before moving onto the more specific categories (e.g. 'Zip') in order to
        increase speed.
      </p>
      {/* State select box */}
      <div className="text-neutral text-left mt-20">States: {territoryOptions?.states.length} options</div>
      <Autocomplete
        options={territoryOptions?.states ?? []}
        isMulti={true}
        onChange={val => {
          console.log(val);
          const tempTerritoryInfo = Object.assign({}, territoryInfo);
          tempTerritoryInfo.states = val ? val.map(st => st.value) : [];
          setTerritoryInfo(tempTerritoryInfo);
          getTerritoryOptionsAsync(tempTerritoryInfo);
        }}
        value={getSelectedValue(territoryOptions?.states, territoryInfo?.states)}
      />

      {/* County select box */}
      <div className="text-neutral text-left mt-20">Counties: {territoryOptions?.counties.length} options</div>
      <Autocomplete
        options={territoryOptions?.counties ?? []}
        isMulti={true}
        onChange={val => {
          const tempTerritoryInfo = Object.assign({}, territoryInfo);
          tempTerritoryInfo.counties = val ? val.map(c => c.value) : [];
          setTerritoryInfo(tempTerritoryInfo);
          getTerritoryOptionsAsync(tempTerritoryInfo);
        }}
        value={getSelectedValue(territoryOptions?.counties, territoryInfo?.counties)}
      />

      {/* Zip codes box */}
      <div className="text-neutral text-left mt-20">Zip codes: {territoryOptions?.zipCodes.length} options</div>
      <Autocomplete
        options={territoryOptions?.zipCodes ?? []}
        isMulti={true}
        onChange={val => {
          const tempTerritoryInfo = Object.assign({}, territoryInfo);
          tempTerritoryInfo.zipCodes = val ? val.map(zc => zc.value) : [];
          setTerritoryInfo(tempTerritoryInfo);
          getTerritoryOptionsAsync(tempTerritoryInfo);
        }}
        value={getSelectedValue(territoryOptions?.zipCodes, territoryInfo?.zipCodes)}
      />

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          marginTop: '12px',
        }}
      >
        <Button
          disabled={isSaving}
          style={{
            // marginLeft: 10,
            backgroundColor: '#17c80d',
            color: '#fff',
          }}
          variant="contained"
          onClick={updateTerritoryInfoAsync}
        >
          {isSaving ? <CircularProgress style={{ fill: '#fff', color: '#fff' }} size={25} /> : 'SAVE'}
        </Button>

        {/* {submitError && <p className={classes.textLabelError}>{submitError}</p>}
        {submitSuccess && redirectOnSuccess && <Redirect to={redirectOnSuccess} />}
        {submitSuccess && <p className={classes.textLabelSuccess}>{submitSuccess}</p>} */}
      </div>
      <p style={{ color: 'rgba(0,0,0,0.5)' }}>
        Tip: if you do not see a zip code that should be contained in the selected states, cities, or counties, the zip
        code may need to be added to the database before continuing.
      </p>
    </div>
  );
});

const formInitialValues = {
  firstName: '',
  middleName: '',
  lastName: '',
  company: '',
  mobilePhone: '',
  homePhone: '',
  email: '',
  userName: '',
  password: '',
  profilePictureInput: '', // used for html input
  profilePictureBase64: '', // used to hold the converted image
  profilePictureURI: '', // used for an existing image
  profilePicturePreview: '', // used to generate a preview URL
  homeAddress: {
    addressLine1: '',
    addressLine2: '',
    addressLine3: '',
    city: '',
    state: '',
    zip: '',
  },
  workAddress: {
    addressLine1: '',
    addressLine2: '',
    addressLine3: '',
    city: '',
    state: '',
    zip: '',
  },
  radius: '',
  strictRadius: false,
  isPreferred: false,
  maxOrders: '',
  skills: [],
  recordType: '',
};

const formValidationSchema = Yup.object().shape({
  firstName: Yup.string().required('Required'),
  middleName: Yup.string().notRequired(),
  lastName: Yup.string().required('Required'),
  company: Yup.string().notRequired(),
  mobilePhone: Yup.string().required('Required'),
  homePhone: Yup.string().notRequired(),
  email: Yup.string()
    .email()
    .required('Required'),
  userName: Yup.string().required('Required'),

  /**
   * Password validation
   * ?https://stackoverflow.com/questions/19605150/regex-for-password-must-contain-at-least-eight-characters-at-least-one-number-a
   */
  password: Yup.string().notRequired(),
  // .required()
  // .matches(
  //   /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&.])[A-Za-z\d@$!%*?&.]{6,}$/,
  //   // /^(?=.*[a-z])(?=.*[A-Z])(?=.*d)(?=.*[@$!%*?&])[A-Za-zd@$!%*?&]{6,}$/,
  //   // 'Password must have a lower case and upper case character, include a number, and be at lesat 6 characters long',
  //   'Minimum six characters, at least one uppercase letter, one lowercase letter, one number and one special character',
  // ),

  // profilePictureInput: '', // used for html input
  profilePictureBase64: Yup.string().notRequired(), // used to hold the converted image
  profilePictureURI: Yup.string().notRequired(), // used for an existing image
  // profilePicturePreview: Yup.string(), // used to generate a preview URL

  homeAddress: Yup.object().shape({
    addressLine1: Yup.string().required('Required'),
    addressLine2: Yup.string().notRequired(),
    addressLine3: Yup.string().notRequired(),
    city: Yup.string().required('Required'),
    state: Yup.string().required('Required'),
    zip: Yup.string()
      .length(5, 'Must be a valid 5 digit zip code')
      .required('Required'),
  }),
  workAddress: Yup.object().shape({
    addressLine1: Yup.string().notRequired(),
    addressLine2: Yup.string().notRequired(),
    addressLine3: Yup.string().notRequired(),
    city: Yup.string().notRequired(),
    state: Yup.string().notRequired(),
    zip: Yup.string().notRequired(),
  }),
  code: Yup.string().required('Required'),
  radius: Yup.string().required('Required'),
  maxOrders: Yup.string().required('Required'),
  strictRadius: Yup.bool(),
  isPreferred: Yup.bool(),
  skills: Yup.array().of(
    Yup.object().shape({
      productCategoryId: Yup.number(),
      productCategoryName: Yup.string(),
      propertyTypeId: Yup.number(),
      propertyTypeName: Yup.string(),
      qualified: Yup.bool(),
    }),
  ),
  recordType: Yup.string().required('Required'),
});

const getInitialFormValues = (initValues, existingValues = {}) => {
  const merged = Object.keys(initValues).reduce((acc, key) => {
    const initialValue = initValues[key];
    const existingValue = existingValues?.[key];

    acc[key] =
      Array.isArray(existingValue) || Array.isArray(initialValue) // if it's an array, take the array value
        ? existingValue || initialValue
        : typeof initValues[key] === 'object' // else - check if it is a spreadable object
        ? getInitialFormValues(initValues[key], existingValues?.[key])
        : // ? {
        //     ...initValues[key],
        //     ...existingValues?.[key],
        //   }
        // : existingValue === undefined // else check if the existing data is undefined and it needs to fallback to the init data values for the form to work
        [null, undefined].includes(existingValue) // else check if the existing data is undefined and it needs to fallback to the init data values for the form to work
        ? initialValue
        : existingValue;

    return acc;
  }, existingValues || {});

  return merged;
};

export const InspectorUsersDetail = withStyles(styles)(({ classes, match, history }) => {
  // PARAMS
  const { userId: mode } = match.params;

  // STATE
  const [profile, setProfile] = useState(null);

  // HOOKS
  const { state, dispatch } = useContext(AppContext);
  
  // Check if user has admin permissions
  const isAdmin = hasPermission([roles.ADMIN, roles.SUPER_ADMIN], state.roles);

  // COMPUTED
  const inCreateMode = mode === 'create';

  const getExistingProfile = async inspectorId => {
    try {
      const inspectorProfile = await getInspectorProfile(inspectorId);

      setProfile(inspectorProfile);
    } catch (error) {
      console.log(error);
    }
  };

  const getSkills = async () => {
    try {
      const skills = await getInspectorProfileSkills();

      setProfile({
        skills,
      });
    } catch (error) {
      console.log(error);
    }
  };

  const showSnackbar = (message, variant) => {
    dispatch({
      type: 'SET_SNACKBAR',
      snackbar: { isOpen: true, message: message, variant: variant },
    });
  };

  const handleFormSubmission = async formValues => {
    try {
      const success = await saveInspectorProfile(formValues);
      const message = inCreateMode ? 'Profile created!' : 'Profile updated!';
      showSnackbar(message, 'success');

      history.goBack();
    } catch (error) {
      showSnackbar(error?.error || 'An error occurred. Please try again.', 'error');
    }
  };

  const renderSkillsLists = (formik, propertyTypeName) => {
    return (
      <>
        <FormControl component="fieldset">
          <FormLabel component="legend">{propertyTypeName}</FormLabel>
          <FieldArray
            name="skills"
            render={arrayHelpers => (
              <FormGroup>
                {formik.values.skills.map((skill, index) => {
                  const shouldShow = skill.propertyTypeName === propertyTypeName;

                  return shouldShow ? (
                    <FormControlLabel
                      key={skill.productCatgeoryName}
                      control={
                        <Checkbox
                          name={`skills[${index}].qualified`}
                          checked={skill.qualified}
                          disabled={!isAdmin}
                          onChange={() => {
                            if (!isAdmin) return; // Prevent changes for non-admin users
                            arrayHelpers.replace(index, {
                              ...skill,
                              qualified: !skill.qualified,
                            });
                          }}
                        />
                      }
                      label={skill.productCategoryName}
                    />
                  ) : null;
                })}
              </FormGroup>
            )}
          />
        </FormControl>
      </>
    );
  };

  useEffect(() => {
    if (inCreateMode) {
      getSkills(mode);
    } else {
      getExistingProfile(mode);
    }
  }, []);

  // setting the wingspanID
  if (profile) {
    profile.wingspanId = 'F' + profile?.inspectorId;
  }

  // Show read-only indicator for non-admin users
  const renderReadOnlyIndicator = () => {
    if (!isAdmin) {
      return (
        <div style={{ 
          backgroundColor: '#fff3cd', 
          border: '1px solid #ffeaa7', 
          borderRadius: '4px', 
          padding: '12px', 
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <Typography variant="body2" style={{ color: '#856404', fontWeight: 'bold' }}>
            Read-Only Mode
          </Typography>
          <Typography variant="body2" style={{ color: '#856404' }}>
            Only administrators can edit inspector details.
          </Typography>
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <div className={'grid-container'} style={{ position: 'relative' }}>
        {/* loading state */}
        {!profile && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              zIndex: 1,
            }}
          >
            <Loading size={100} style={{ position: 'absolute', top: 200, color: 'white' }} />
          </div>
        )}

        <Formik
          initialValues={getInitialFormValues(formInitialValues, profile)}
          onSubmit={handleFormSubmission}
          validationSchema={formValidationSchema}
          enableReinitialize
        >
          {formik => {
            // window.formik = formik;

            return (
              <Form>
                {renderReadOnlyIndicator()}
                <Grid container spacing={2}>
                  <FormControlWrapper>
                    <FormTextField name="firstName" label="First Name" formik={formik} required isReadOnly={!isAdmin} />
                    <span className={classes.errorMessage}>
                      <ErrorMessage name="firstName" />
                    </span>
                  </FormControlWrapper>
                  <FormControlWrapper>
                    <FormTextField name="middleName" label="Middle Name" formik={formik} isReadOnly={!isAdmin} />
                    <span className={classes.errorMessage}>
                      <ErrorMessage name="middleName" />
                    </span>
                  </FormControlWrapper>
                  <FormControlWrapper>
                    <FormTextField name="lastName" label="Last Name" formik={formik} required isReadOnly={!isAdmin} />
                    <span className={classes.errorMessage}>
                      <ErrorMessage name="lastName" />
                    </span>
                  </FormControlWrapper>

                  <FormControlWrapper>
                    <FormTextField name="company" label="Company" formik={formik} isReadOnly={!isAdmin} />
                    <span className={classes.errorMessage}>
                      <ErrorMessage name="company" />
                    </span>
                  </FormControlWrapper>
                  <FormControlWrapper>
                    <FormTextField name="mobilePhone" label="Mobile Phone" formik={formik} required isReadOnly={!isAdmin} />
                    <span className={classes.errorMessage}>
                      <ErrorMessage name="mobilePhone" />
                    </span>
                  </FormControlWrapper>
                  <FormControlWrapper>
                    <FormTextField name="homePhone" label="Home Phone" formik={formik} isReadOnly={!isAdmin} />
                    <span className={classes.errorMessage}>
                      <ErrorMessage name="homePhone" />
                    </span>
                  </FormControlWrapper>

                  <FormControlWrapper>
                    <FormTextField name="email" label="Email" formik={formik} required isReadOnly={!isAdmin} />
                    <span className={classes.errorMessage}>
                      <ErrorMessage name="email" />
                    </span>
                  </FormControlWrapper>

                  <FormControlWrapper>
                    <FormTextField name="userName" label="Username" formik={formik} required isReadOnly={!isAdmin} />
                    <span className={classes.errorMessage}>
                      <ErrorMessage name="userName" />
                    </span>
                  </FormControlWrapper>

                  {/* <FormControlWrapper>
                    <Select
                      name="recordType"
                      options={[
                        {
                          value: '',
                          label: 'Record Type',
                          disabled: true,
                        },
                        {
                          value: 'Active',
                          label: 'Active',
                        },
                        {
                          value: 'Inactive',
                          label: 'Inactive',
                        },
                      ]}
                      optionLabelKey="label"
                      optionValueKey="value"
                      value={formik.values.recordType}
                      onChange={value => {
                        formik.setFieldValue('recordType', value);
                      }}
                      inputStyle={{ height: 48 }}
                      label="Record Type"
                    /> 
                  </FormControlWrapper> */}
                  <FormControlWrapper>
                    <FormTextField
                      name="password"
                      // type="password"
                      label={inCreateMode ? 'Password' : 'Password Update'}
                      // placeholder={inCreateMode ? '' : 'Only enabled when creating'}
                      formik={formik}
                      isReadOnly={!isAdmin}
                      // disabled={!inCreateMode}
                    />
                    <span className={classes.errorMessage}>
                      <ErrorMessage name="password" />
                    </span>
                  </FormControlWrapper>

                  <FormControlWrapper>
                    <FormTextField name="wingspanId" label="Wingspan ID" defaultValue=" " formik={''} disabled />
                  </FormControlWrapper>

                  <FormControlWrapper>
                    <FormTextField
                      name="code"
                      label="Inspector Code"
                      defaultValue=" "
                      formik={''}
                      isReadOnly={!isAdmin}
                      required
                      inputProps={{ style: { textTransform: 'uppercase' } }}
                      onChange={e => {
                        const val = e.target.value.toUpperCase();
                        if (formik && formik.setFieldValue) {
                          formik.setFieldValue('code', val);
                        }
                      }}
                    />
                  </FormControlWrapper>

                  <FormControlWrapper>
                    <FormTextField name="notes" label="Notes" multiline maxRows={4} formik={formik} defaultValue=" " isReadOnly={!isAdmin} />
                  </FormControlWrapper>


                  <FormControlWrapper>
                    <Typography variant="subtitle1" style={{ marginRight: 10, textAlign: 'left' }}>
                      Record Type *
                    </Typography>
                    <RadioGroup
                      name="recordType"
                      value={formik.values.recordType}
                      onChange={formik.handleChange}
                      style={{ flexDirection: 'row', alignItems: 'center' }}
                    >
                      <FormControlLabel value="Active" control={<Radio disabled={!isAdmin} />} label="Active" />
                      <FormControlLabel value="Inactive" control={<Radio disabled={!isAdmin} />} label="Inactive" />
                    </RadioGroup>
                    <Typography variant="subtitle2" style={{ marginRight: 10, textAlign: 'left' }}>
                      Setting a record "Inactive" will attempt to reassign any active orders assigned to this Field Rep
                    </Typography>
                    <span className={classes.errorMessage}>
                      <ErrorMessage name="recordType" />
                    </span>
                  </FormControlWrapper>

                  <FormControlWrapper style={{ display: 'flex' }}>
                    <FormCheckbox name="isPreferred" label="Preferred Inspector" formik={formik} isReadOnly={!isAdmin} />
                  </FormControlWrapper>

                  <FormControlWrapper></FormControlWrapper>

                  <FormControlWrapper>
                    <Typography variant="subtitle1" style={{ marginRight: 10, textAlign: 'left' }}>
                      Profile Picture
                    </Typography>
                    <FormTextField
                      name="profilePictureInput"
                      type="file"
                      formik={formik}
                      isReadOnly={!isAdmin}
                      onChange={async evt => {
                        if (!isAdmin) return; // Prevent file upload for non-admin users
                        const file = evt.target.files[0];
                        const url = URL.createObjectURL(file);
                        const base64 = await toBase64(file);
                        const trimmed = base64.replace(/^data:image\/(png|jpg|jpeg);base64,/, '');

                        formik.setFieldValue('profilePictureBase64', trimmed);
                        formik.setFieldValue('profilePicturePreview', url);

                        formik.handleChange(evt);
                      }}
                    />
                    <span className={classes.errorMessage}>
                      <ErrorMessage name="profilePictureInput" />
                    </span>
                  </FormControlWrapper>
                  <FormControlWrapper></FormControlWrapper>
                  <FormControlWrapper></FormControlWrapper>

                  <FormControlWrapper>
                    <div>
                      <span>Image Preview</span>
                    </div>
                    <div style={{ border: '2px dashed gray', height: 200, padding: 10, position: 'relative' }}>
                      <img src={formik.values.profilePicturePreview} style={{ height: '100%' }} />
                      {!!formik.values.profilePicturePreview && (
                        <Close
                          style={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            backgroundColor: 'white',
                            cursor: 'pointer',
                          }}
                          onClick={() => {
                            formik.setFieldValue('profilePictureInput', '');
                            formik.setFieldValue('profilePictureBase64', '');
                            formik.setFieldValue('profilePicturePreview', '');
                          }}
                        />
                      )}
                    </div>
                  </FormControlWrapper>
                  <FormControlWrapper>
                    <span>Existing Image</span>
                    <div style={{ border: '2px dashed gray', height: 200, padding: 10, position: 'relative' }}>
                      <img src={formik.values.profilePictureURI} style={{ height: '100%' }} />
                      {/* <Close
                        style={{ position: 'absolute', top: 0, right: 0, backgroundColor: 'white', cursor: 'pointer' }}
                        onClick={() => {
                          formik.setFieldValue('profilePictureURI', '');
                        }}
                      /> */}
                    </div>
                  </FormControlWrapper>
                  <FormControlWrapper></FormControlWrapper>
                </Grid>

                <Divider style={{ margin: '25px 0' }} />

                <Grid container spacing={2}>
                  {/* HOME ADDRESS */}
                  <Grid item xs={12} container justifyContent="flex-start">
                    <span>Home</span>
                  </Grid>
                  <FormControlWrapper>
                    <FormTextField name="homeAddress.addressLine1" label="Address Line 1" formik={formik} required isReadOnly={!isAdmin} />
                    <span className={classes.errorMessage}>
                      <ErrorMessage name="homeAddress.addressLine1" />
                    </span>
                  </FormControlWrapper>
                  <FormControlWrapper>
                    <FormTextField name="homeAddress.addressLine2" label="Address Line 2" formik={formik} isReadOnly={!isAdmin} />
                    <span className={classes.errorMessage}>
                      <ErrorMessage name="homeAddress.addressLine2" />
                    </span>
                  </FormControlWrapper>
                  <FormControlWrapper>
                    <FormTextField name="homeAddress.addressLine3" label="Address Line 3" formik={formik} isReadOnly={!isAdmin} />
                    <span className={classes.errorMessage}>
                      <ErrorMessage name="homeAddress.addressLine3" />
                    </span>
                  </FormControlWrapper>

                  <FormControlWrapper>
                    <FormTextField name="homeAddress.city" label="City" formik={formik} required isReadOnly={!isAdmin} />
                    <span className={classes.errorMessage}>
                      <ErrorMessage name="homeAddress.city" />
                    </span>
                  </FormControlWrapper>
                  <FormControlWrapper>
                    <FormTextField name="homeAddress.state" label="State" formik={formik} required isReadOnly={!isAdmin} />
                    <span className={classes.errorMessage}>
                      <ErrorMessage name="homeAddress.state" />
                    </span>
                  </FormControlWrapper>
                  <FormControlWrapper>
                    <FormTextField name="homeAddress.zip" label="Zip" formik={formik} required isReadOnly={!isAdmin} />
                    <span className={classes.errorMessage}>
                      <ErrorMessage name="homeAddress.zip" />
                    </span>
                  </FormControlWrapper>

                  {/* WORK ADDRESS */}
                  <Grid item xs={12} container justify="space-between" alignItems="center">
                    <span>Work</span>
                    {/* <Checkbox /> */}
                  </Grid>
                  <FormControlWrapper>
                    <FormTextField name="workAddress.addressLine1" label="Address Line 1" formik={formik} isReadOnly={!isAdmin} />
                    <span className={classes.errorMessage}>
                      <ErrorMessage name="workAddress.addressLine1" />
                    </span>
                  </FormControlWrapper>
                  <FormControlWrapper>
                    <FormTextField name="workAddress.addressLine2" label="Address Line 2" formik={formik} isReadOnly={!isAdmin} />
                    <span className={classes.errorMessage}>
                      <ErrorMessage name="workAddress.addressLine2" />
                    </span>
                  </FormControlWrapper>
                  <FormControlWrapper>
                    <FormTextField name="workAddress.addressLine3" label="Address Line 3" formik={formik} isReadOnly={!isAdmin} />
                    <span className={classes.errorMessage}>
                      <ErrorMessage name="workAddress.addressLine3" />
                    </span>
                  </FormControlWrapper>

                  <FormControlWrapper>
                    <FormTextField name="workAddress.city" label="City" formik={formik} isReadOnly={!isAdmin} />
                    <span className={classes.errorMessage}>
                      <ErrorMessage name="workAddress.city" />
                    </span>
                  </FormControlWrapper>
                  <FormControlWrapper>
                    <FormTextField name="workAddress.state" label="State" formik={formik} isReadOnly={!isAdmin} />
                    <span className={classes.errorMessage}>
                      <ErrorMessage name="workAddress.state" />
                    </span>
                  </FormControlWrapper>
                  <FormControlWrapper>
                    <FormTextField name="workAddress.zip" label="Zip" formik={formik} isReadOnly={!isAdmin} />
                    <span className={classes.errorMessage}>
                      <ErrorMessage name="workAddress.zip" />
                    </span>
                  </FormControlWrapper>

                  <FormControlWrapper>
                    <FormTextField name="maxOrders" label="Max Orders" formik={formik} required isReadOnly={!isAdmin} />
                    <span className={classes.errorMessage}>
                      <ErrorMessage name="maxOrders" />
                    </span>
                  </FormControlWrapper>
                  <FormControlWrapper>
                    <FormTextField name="radius" label="Work Radius (miles)" formik={formik} required isReadOnly={!isAdmin} />
                    <span className={classes.errorMessage}>
                      <ErrorMessage name="radius" />
                    </span>
                  </FormControlWrapper>
                  <FormControlWrapper style={{ display: 'flex' }}>
                    <FormCheckbox name="strictRadius" label="Strict" formik={formik} isReadOnly={!isAdmin} />
                  </FormControlWrapper>
                </Grid>

                <Divider style={{ margin: '25px 0' }} />

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h4" style={{ flex: 1 }}>
                      Skills
                    </Typography>
                  </Grid>

                  <FormControlWrapper md={6}>{renderSkillsLists(formik, 'Residential')}</FormControlWrapper>
                  <FormControlWrapper md={6}>{renderSkillsLists(formik, 'Commercial')}</FormControlWrapper>

                  <FormControlWrapper md={6}></FormControlWrapper>
                </Grid>

                {isAdmin && (
                  <div style={{ width: '100%', textAlign: 'center', marginTop: 15 }}>
                    {formik.isSubmitting ? (
                      <CircularProgress size={24} />
                    ) : (
                      <Button
                        type="submit"
                        size="medium"
                        color="primary"
                        variant="outlined"
                        onClick={formik.submitForm}
                        // color={formik.isValid ? 'action' : 'default'}
                        disabled={!formik.isValid || formik.isSubmitting}
                      >
                        Save
                      </Button>
                    )}
                  </div>
                )}
              </Form>
            );
          }}
        </Formik>
      </div>
    </>
  );
});
